import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, Text } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { sandboxBridge } from './appSandboxBridge';

// How long to coalesce back-to-back preview reloads (WEB-7787). Small enough
// that an explicit Save/reload still feels instant, large enough to swallow a
// burst of reloadKey bumps that arrive within the same interaction.
const RELOAD_DEBOUNCE_MS = 300;

export interface PageIframeInit {
	userId: number | null;
	/** The tenant/customer id — the `:customerId` segment in in-app hash routes
	 * (`#/<customerId>/accounts/<id>/`). Lets a Page build working deep links
	 * for `useNavigation().open(...)`. */
	customerId: number | null;
	clientId: number | null;
	orderId: number | null;
	projectId: number | null;
	language: string | null;
	currentRoute: string | null;
}

export type PickedElement = {
	label: string;
	kind: string;
	tag: string;
	/** Ancestor chain, nearest last (`table > tbody > tr > td`). */
	path: string;
	/** Index among same-tag siblings, e.g. `3/5`. Empty when it is the only one. */
	position: string;
	/** Nearest stable text — a cell's column header, or the heading above. */
	context: string;
};

interface Props {
	slug: string;
	init: PageIframeInit;
	className?: string;
	style?: React.CSSProperties;
	title?: string;
	/**
	 * When true, the iframe loads the page's current (possibly unpublished)
	 * compiled bundle from `/api/v2/agentPage/:slug/draft.js` instead of the
	 * public `/bundle.js`. Used by the WEB-6723 authoring shell. Defaults to
	 * false so public-URL viewers keep seeing the active bundle.
	 */
	draft?: boolean;
	/**
	 * Bumping this forces the iframe to reload — used by the authoring shell
	 * to re-fetch the compiled bundle after a save without unmounting the
	 * sandbox bridge.
	 */
	reloadKey?: number;
	onCrash?: (msg: { message: string; stack?: string; componentStack?: string }) => void;
	/**
	 * Pick mode (WEB-10047): the shell outlines what is under the pointer and
	 * reports the element the user clicks. Authoring only — a published page is
	 * never mounted with it.
	 */
	picking?: boolean;
	onElementPicked?: (picked: PickedElement) => void;
	/**
	 * Bumping this replays the last change flash (WEB-10047) — the outline is 1.6s
	 * long and fires in a pane the user is usually not looking at.
	 */
	replayFlashKey?: number;
	/** Fires when a change flash has run, so the host can enable its replay control. */
	onChangeFlashed?: (info: { replayable: boolean }) => void;
}

/**
 * Renders a sandboxed iframe pointed at the Pages shell.
 *
 * Phase 1 (this slice) runs with `sandbox="allow-scripts allow-same-origin"`
 * so the shell can authenticate fetches against the Upsales API with the
 * user's session cookie. Per the project doc this is the "internal-MVP
 * relaxation" — Pages are authored by Upsales staff, so the lighter
 * sandbox is acceptable.
 *
 * `allow-downloads` joins those two (WEB-11454). Without it a Page cannot
 * hand the user a file at all: an anchor `download`, a `data:` URL or a blob
 * is dropped by the browser with no error and no event, so a page that
 * exports a CSV just appears to do nothing. It grants no reach the pair above
 * does not already give — a frame with scripts AND same-origin can fetch the
 * same bytes and post them to the parent — it only lets the user receive
 * them. Same call as FEA-54 made for the app iframes, which have carried
 * `allow-downloads` since (`uiElementsHelper.ts`, `uiElements.ts`).
 *
 * Phase 2 (WEB-6726) moves this to a true cross-origin subdomain so the
 * iframe gets an unguessable opaque origin, with per-page CSP nonces and
 * a session-token RPC bridge instead of cookie-credentialed fetches.
 *
 * Even with `allow-same-origin`, the iframe still:
 *   - Cannot navigate the top frame or open popups directly (no
 *     allow-top-navigation, no allow-popups). Anchor clicks and
 *     `window.location = …` from inside a Page are blocked. To leave the
 *     Page a author calls the SDK's `useNavigation().open(url)` /
 *     `openEditor(entity, id)`, which post a message the parent (this
 *     trusted, un-sandboxed window) acts on via the sandbox bridge — so the
 *     sandbox stays sealed while in-app links/editors still work.
 *   - Cannot submit forms (no allow-forms).
 *   - Can only escalate to the parent via the postMessage bridge.
 *
 * The parent posts an `init` message after the iframe loads, and re-posts
 * whenever `init` changes so the page can react to user/route updates.
 */
export default function PageIframe({
	slug,
	init,
	className,
	style,
	title,
	draft,
	reloadKey,
	onCrash,
	picking,
	onElementPicked,
	replayFlashKey,
	onChangeFlashed
}: Props) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const loadedRef = useRef(false);
	// `reloadKey` is NOT in the src: changing the src navigates the iframe (white
	// flash). Instead we post a `reload` message so the shell re-fetches + re-
	// renders the bundle in place — no flash (WEB-7757).
	const src = `/sandboxed-pages/${encodeURIComponent(slug)}${draft ? '?draft=1' : ''}`;
	const { t } = useTranslation();
	// The src the shell reported as not available, so a different page starts out showing.
	const [notFoundSrc, setNotFoundSrc] = useState<string | null>(null);

	const postInit = useCallback(() => {
		const win = iframeRef.current?.contentWindow;
		if (!win) return;
		win.postMessage({ type: 'init', ...init }, '*');
	}, [init]);

	const onLoad = useCallback(() => {
		loadedRef.current = true;
		// Register this iframe with the RPC bridge as soon as it loads. The
		// bridge listens for postMessages from registered iframes and forwards
		// SDK tool calls (tools.crmMutation, tools.readCRM, etc.) to the
		// `/api/v2/agentPage/rpc` endpoint. WEB-6721.
		sandboxBridge.register(iframeRef.current?.contentWindow ?? null, slug);
		postInit();
		// The pick flag is posted on change, which a shell that had not booted yet
		// would have dropped — restate it once the shell is listening.
		if (picking) iframeRef.current?.contentWindow?.postMessage({ type: 'pick', on: true }, '*');
	}, [postInit, slug, picking]);

	useEffect(() => {
		// Unregister on unmount or slug change. Re-registration happens on
		// the next onLoad. We capture the window inside the effect so the
		// cleanup function unregisters the *same* iframe we registered.
		const win = iframeRef.current?.contentWindow;
		return () => {
			sandboxBridge.unregister(win ?? null);
		};
	}, [slug]);

	useEffect(() => {
		if (loadedRef.current) postInit();
	}, [postInit]);

	// Ask the shell to re-fetch + re-render the bundle in place when the parent
	// bumps reloadKey (e.g. after a build). Skipped on first mount / before load
	// (the initial fetch happens on the shell's own load).
	//
	// Debounced (WEB-7787): a reload re-evaluates the bundle and re-runs the page
	// component from scratch, re-firing every readCRM the page makes on mount. If
	// reloadKey bumps several times in quick succession (autosave landing while a
	// chat turn also applies code, rapid saves, etc.) we only want the last one to
	// hit — each new bump cancels the pending reload and schedules a fresh one, so
	// a burst collapses into a single recompile.
	useEffect(() => {
		if (!loadedRef.current || reloadKey === undefined) return;
		const id = window.setTimeout(() => {
			iframeRef.current?.contentWindow?.postMessage({ type: 'reload', r: reloadKey }, '*');
		}, RELOAD_DEBOUNCE_MS);
		return () => window.clearTimeout(id);
	}, [reloadKey]);

	useEffect(() => {
		if (!onCrash) return;
		const handler = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow) return;
			const data = event.data;
			if (!data || typeof data !== 'object' || data.type !== 'page.crash') return;
			onCrash({ message: data.message, stack: data.stack, componentStack: data.componentStack });
		};
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [onCrash]);

	useEffect(() => {
		// Re-posted whenever the flag changes AND on load, since a shell that has
		// not booted yet would drop the message.
		if (loadedRef.current) {
			iframeRef.current?.contentWindow?.postMessage({ type: 'pick', on: picking === true }, '*');
		}
	}, [picking]);

	useEffect(() => {
		if (!loadedRef.current || replayFlashKey === undefined) return;
		iframeRef.current?.contentWindow?.postMessage({ type: 'flashAgain' }, '*');
	}, [replayFlashKey]);

	useEffect(() => {
		if (!onChangeFlashed) return;
		const handler = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow) return;
			const data = event.data;
			if (!data || typeof data !== 'object' || data.type !== 'page.changeFlashed') return;
			onChangeFlashed({ replayable: data.replayable === true });
		};
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [onChangeFlashed]);

	useEffect(() => {
		if (!onElementPicked) return;
		const handler = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow) return;
			const data = event.data;
			if (!data || typeof data !== 'object' || data.type !== 'page.elementPicked') return;
			onElementPicked({
				label: String(data.label ?? ''),
				kind: String(data.kind ?? ''),
				tag: String(data.tag ?? ''),
				path: String(data.path ?? ''),
				position: String(data.position ?? ''),
				context: String(data.context ?? '')
			});
		};
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [onElementPicked]);

	useEffect(() => {
		const handler = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow) return;
			if (event.data?.type === 'page.notFound') setNotFoundSrc(src);
		};
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [src]);

	if (notFoundSrc === src) {
		return (
			<Flex
				className={className}
				justifyContent="center"
				alignItems="center"
				style={{ height: '100%', ...style }}
			>
				<Text color="grey-10" center>
					{t('assistant.pages.unavailable')}
				</Text>
			</Flex>
		);
	}

	return (
		<iframe
			ref={iframeRef}
			src={src}
			sandbox="allow-scripts allow-same-origin allow-downloads"
			onLoad={onLoad}
			className={className}
			title={title || `Upsales Page: ${slug}`}
			style={{ border: 0, width: '100%', height: '100%', display: 'block', ...style }}
		/>
	);
}
